import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { CheckboxItem } from 'components/core/checkbox-group';
import { RadioButtonItem } from 'components/core/radio-button-group';

import client from 'api/apollo-client';
import { SubsidyOverviewFieldsFragment } from 'api/graphql-types';
import { getServerPageGetSubsidyFilters } from 'api/graphql-helper';
import { useAppSelector } from 'redux/store';
import { setGroups } from 'redux/stores/groups';
import { setKinds } from 'redux/stores/kinds';
import { setScales } from 'redux/stores/scales';
import { IFilters } from 'components/filter-sidebar';
import { useScale } from '../queries/use-scale';
import { useGroup } from '../queries/use-groups';
import { useKind, useTheme } from 'utils/hooks/queries';

const useSubsidyFilters = (
	module: SubsidyOverviewFieldsFragment,
	onChange: (themeIds: string[], kindIds: string[], scaleIds: string[], groupIds: string[]) => void
): IFilters => {
	const dispatch = useDispatch();
	const router = useRouter();

	const { themes } = useAppSelector((state) => state.themes);
	const { kinds } = useAppSelector((state) => state.kinds);
	const { scales } = useAppSelector((state) => state.scales);
	const { groups } = useAppSelector((state) => state.groups);

	const { selectedThemes, setSelectedThemesParam } = useTheme();
	const { selectedScales, setSelectedScalesParam } = useScale();
	const { selectedKinds, setSelectedKindsParam } = useKind(kinds);
	const { selectedGroups, setSelectedGroupsParam } = useGroup();

	const themesFilter = themes &&
		themes.length > 0 && {
			id: 'selectedThemes',
			type: 'checkbox',
			title: module.themeFilterText || '',
			onChange: (items: CheckboxItem[], active: boolean): void => {
				if (active) {
					const mergedThemes = [...selectedThemes, ...items];
					setSelectedThemesParam(mergedThemes);
					onChange(
						mergedThemes.map((item) => String(item.id)),
						selectedKinds.map((item) => String(item.id)),
						selectedScales.map((item) => String(item.id)),
						selectedGroups.map((item) => String(item.id))
					);
				} else {
					const itemIds = new Set(items.map((newItem) => newItem.id));
					const newThemes = selectedThemes.filter((selectedItem) => !itemIds.has(selectedItem.id));
					setSelectedThemesParam(newThemes);
					onChange(
						newThemes.map((item) => String(item.id)),
						selectedKinds.map((item) => String(item.id)),
						selectedScales.map((item) => String(item.id)),
						selectedGroups.map((item) => String(item.id))
					);
				}
			},
			value: selectedThemes,
			showMore: module.showMoreThemesText,
			showLess: module.showLessThemesText,
			options: themes?.map((theme) => ({
				id: Number(theme.id),
				label: theme?.attributes?.title
			})) as CheckboxItem[]
		};

	// Adds a label identifier to all option id's
	// This must be done to make the htmlFor on the labels work
	const groupFilter = groups.length > 0 && {
		id: 'targetGroup',
		type: 'radio',
		title: module.targetGroupFilterText || '',
		onChange: (item: RadioButtonItem): void => {
			setSelectedGroupsParam(item);
			onChange(
				selectedThemes.map((theme) => String(theme.id)),
				selectedKinds.map((kind) => String(kind.id)),
				selectedScales.map((scale) => String(scale.id)),
				item.id ? [String(item.id.split('-')[1])] : []
			);
		},
		value: { id: `targetGroup-${selectedGroups[0]?.id}`, label: selectedGroups[0]?.label },
		options: groups.map((item) => ({
			id: `targetGroup-${item.id}`,
			label: item?.name
		})) as RadioButtonItem[]
	};

	const kindFilter = kinds.length > 0 && {
		id: 'kind',
		type: 'radio',
		title: module.kindFilterText,
		onChange: (item: RadioButtonItem): void => {
			setSelectedKindsParam(item);
			onChange(
				selectedThemes.map((theme) => String(theme.id)),
				item.id ? [String(item.id.split('-')[1])] : [],
				selectedScales.map((scale) => String(scale.id)),
				selectedGroups.map((group) => String(group.id))
			);
		},
		value: { id: `kind-${selectedKinds[0]?.id}`, label: selectedKinds[0]?.label },
		options: kinds.map((item) => ({
			id: `kind-${item.id}`,
			label: item?.name
		})) as RadioButtonItem[]
	};

	const scaleFilter = scales.length > 0 && {
		id: 'scale',
		type: 'radio',
		title: module.scaleFilterText,
		onChange: (item: RadioButtonItem): void => {
			setSelectedScalesParam(item);
			onChange(
				selectedThemes.map((theme) => String(theme.id)),
				selectedKinds.map((kind) => String(kind.id)),
				item.id ? [String(item.id.split('-')[1])] : [],
				selectedGroups.map((group) => String(group.id))
			);
		},
		value: { id: `scale-${selectedScales[0]?.id}`, label: selectedScales[0]?.label },
		options: scales.map((item) => ({
			id: `scale-${item.id}`,
			label: item?.name
		})) as RadioButtonItem[]
	};

	const getFilters = async (): Promise<void> => {
		if (kinds && scales && groups) return;

		const { subsidyGroups, subsidyKinds, subsidyScales } = await (
			await getServerPageGetSubsidyFilters({ variables: { locale: router.locale } }, client)
		).props.data;

		if (subsidyGroups) {
			dispatch(
				setGroups(
					subsidyGroups.data.map((item) => ({
						locale: item.attributes?.locale || '',
						name: item.attributes?.name || '',
						id: item.id || ''
					}))
				)
			);
		}

		if (subsidyKinds) {
			dispatch(
				setKinds(
					subsidyKinds.data.map((item) => ({
						locale: item.attributes?.locale || '',
						name: item.attributes?.name || '',
						id: item.id || ''
					}))
				)
			);
		}

		if (subsidyScales) {
			dispatch(
				setScales(
					subsidyScales.data.map((item) => ({
						locale: item.attributes?.locale || '',
						name: item.attributes?.name || '',
						id: item.id || ''
					}))
				)
			);
		}
	};

	useEffect(() => {
		getFilters();
	}, []);

	// Adds all filters and removes empty filters
	return [themesFilter, groupFilter, kindFilter, scaleFilter].filter((item) => item !== false) as IFilters;
};

export default useSubsidyFilters;
